import React from 'react';

const sliders = [
    { label: 'Soreness', value: 6, max: 10, color: '#f97316' },
    { label: 'RPE', value: 7, max: 10, color: '#f97316' },
    { label: 'Motivation', value: 8, max: 10, color: '#22c55e' },
    { label: 'Mental Fatigue', value: 4, max: 10, color: '#eab308' },
];

const selectedRegions = [
    { label: 'L Hamstring', x: 37, y: 134, w: 14, h: 28, color: '#f97316', back: true },
    { label: 'Lower Back', x: 50, y: 88, w: 26, h: 18, color: '#eab308', back: true },
];

const BodySilhouette: React.FC = () => (
    <g fill="#525252" opacity={0.3}>
        <ellipse cx="50" cy="18" rx="12" ry="14" />
        <rect x="45" y="32" width="10" height="8" rx="2" />
        <rect x="33" y="40" width="34" height="60" rx="5" />
        <rect x="20" y="44" width="11" height="42" rx="4" />
        <rect x="69" y="44" width="11" height="42" rx="4" />
        <rect x="34" y="102" width="14" height="54" rx="4" />
        <rect x="52" y="102" width="14" height="54" rx="4" />
        <rect x="34" y="158" width="13" height="36" rx="4" />
        <rect x="53" y="158" width="13" height="36" rx="4" />
    </g>
);

/* Tap-target regions shown as dashed outlines on the body */
const TapRegions: React.FC<{ view: 'front' | 'back' }> = ({ view }) => {
    const frontTargets = [
        { x: 50, y: 18, w: 26, h: 30 },   // head/neck
        { x: 26, y: 52, w: 18, h: 16 },   // L shoulder
        { x: 74, y: 52, w: 18, h: 16 },   // R shoulder
        { x: 36, y: 105, w: 16, h: 14 },  // L hip
        { x: 64, y: 105, w: 16, h: 14 },  // R hip
        { x: 37, y: 128, w: 14, h: 28 },  // L quad
        { x: 63, y: 128, w: 14, h: 28 },  // R quad
        { x: 37, y: 158, w: 13, h: 12 },  // L knee
        { x: 63, y: 158, w: 13, h: 12 },  // R knee
    ];
    const backTargets = [
        { x: 50, y: 62, w: 28, h: 22 },   // upper back
        { x: 50, y: 88, w: 26, h: 18 },   // lower back
        { x: 37, y: 134, w: 14, h: 28 },  // L hamstring
        { x: 63, y: 134, w: 14, h: 28 },  // R hamstring
    ];

    const targets = view === 'front' ? frontTargets : backTargets;
    return (
        <>
            {targets.map((t, i) => (
                <rect
                    key={i}
                    x={t.x - t.w / 2} y={t.y - t.h / 2}
                    width={t.w} height={t.h} rx={3}
                    fill="none" stroke="#525252" strokeWidth={0.5} strokeDasharray="2 2"
                />
            ))}
        </>
    );
};

const MockSessionCheckIn: React.FC = () => (
    <div className="border border-neutral-700 bg-neutral-900/80 max-w-md mx-auto">
        {/* Browser chrome */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-neutral-700 bg-neutral-900">
            <div className="flex gap-1.5">
                <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
                <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
                <div className="w-2.5 h-2.5 rounded-full bg-neutral-600" />
            </div>
            <div className="flex-1 mx-4">
                <div className="bg-neutral-800 h-5 flex items-center px-3 text-[10px] text-gray-500 font-mono">
                    app.cgmsports.com/check-in
                </div>
            </div>
        </div>

        {/* Check-in content */}
        <div className="p-5 space-y-5">
            {/* Header */}
            <div>
                <div className="text-sm font-bold text-white">Daily Check-In</div>
                <div className="text-[11px] text-gray-500 mt-0.5">Jordan Chen &middot; Pre-Practice</div>
            </div>

            {/* Sliders */}
            <div className="space-y-4">
                {sliders.map((s) => (
                    <div key={s.label} className="space-y-1.5">
                        <div className="flex items-center justify-between text-[11px]">
                            <span className="text-gray-300">{s.label}</span>
                            <span className="font-mono font-bold" style={{ color: s.color }}>{s.value}/{s.max}</span>
                        </div>
                        <div className="relative h-2 bg-neutral-800 w-full">
                            <div className="h-full" style={{ width: `${(s.value / s.max) * 100}%`, backgroundColor: s.color }} />
                            {/* Thumb */}
                            <div
                                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 border-2 bg-neutral-900"
                                style={{
                                    left: `calc(${(s.value / s.max) * 100}% - 7px)`,
                                    borderColor: s.color,
                                }}
                            />
                        </div>
                    </div>
                ))}
            </div>

            {/* Regional pain section */}
            <div className="border-t border-neutral-700 pt-4">
                <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-3">Tap regions with pain</div>
                <div className="flex gap-3 justify-center">
                    {/* Front view */}
                    <div className="text-center">
                        <svg viewBox="0 0 100 200" className="w-[70px] h-[140px]">
                            <BodySilhouette />
                            <TapRegions view="front" />
                        </svg>
                        <span className="text-[10px] text-gray-500">Front</span>
                    </div>
                    {/* Back view */}
                    <div className="text-center">
                        <svg viewBox="0 0 100 200" className="w-[70px] h-[140px]">
                            <BodySilhouette />
                            <TapRegions view="back" />
                            {selectedRegions.filter(r => r.back).map(r => (
                                <g key={r.label}>
                                    <rect
                                        x={r.x - r.w / 2} y={r.y - r.h / 2}
                                        width={r.w} height={r.h} rx={3}
                                        fill={r.color} fillOpacity={0.35}
                                        stroke={r.color} strokeWidth={1.5}
                                    />
                                </g>
                            ))}
                        </svg>
                        <span className="text-[10px] text-gray-500">Back</span>
                    </div>
                </div>

                {/* Selected regions */}
                <div className="mt-3 space-y-1.5">
                    {selectedRegions.map(r => (
                        <div key={r.label} className="flex items-center justify-between bg-neutral-800/50 border border-neutral-700/50 px-3 py-1.5 text-[11px]">
                            <span className="text-gray-300">{r.label}</span>
                            <span className="text-gray-500">Pain level: <span className="font-bold" style={{ color: r.color }}>
                                {r.label === 'L Hamstring' ? '6' : '5'}/10
                            </span></span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Submit button */}
            <button
                className="w-full py-2.5 text-sm font-semibold text-black bg-white hover:bg-gray-200 transition-colors cursor-default"
            >
                Submit Check-In
            </button>
        </div>
    </div>
);

export default MockSessionCheckIn;
